/**
 * `createWritable` for browsers without it (Safari before 26, on an iPad), so every file Cutroom writes in the
 * browser's own storage (projects kept in the browser, models, caches) goes through the same code as on a computer.
 * What it gives back works both ways the browser's own does: as a WritableStream (the exporter writes through one) and
 * with write / seek / truncate / close straight on it. The writing itself happens in a worker (opfs.worker.ts).
 * Installed once, at start, only where it's missing.
 */

type Chunk =
  | Blob
  | BufferSource
  | string
  | { type: 'write'; position?: number; data: Blob | BufferSource | string }
  | { type: 'seek'; position: number }
  | { type: 'truncate'; size: number };

let worker: Worker | null = null;
let rid = 0;
let fid = 0;
const waiting = new Map<number, { ok: () => void; no: (e: Error) => void }>();

function call(msg: Record<string, unknown>, transfer: Transferable[] = []): Promise<void> {
  worker ??= (() => {
    const w = new Worker(new URL('./opfs.worker.ts', import.meta.url), { type: 'module' });
    w.onmessage = (e: MessageEvent<{ rid: number; ok: boolean; error?: string }>) => {
      const k = waiting.get(e.data.rid);
      waiting.delete(e.data.rid);
      if (!k) return;
      if (e.data.ok) k.ok();
      else k.no(new DOMException(e.data.error ?? 'The file couldn’t be written.', 'InvalidStateError'));
    };
    return w;
  })();
  const r = ++rid;
  return new Promise((ok, no) => {
    waiting.set(r, { ok, no });
    worker!.postMessage({ ...msg, rid: r }, transfer);
  });
}

const bytesOf = async (d: Blob | BufferSource | string): Promise<ArrayBuffer> => {
  if (typeof d === 'string') return new TextEncoder().encode(d).buffer as ArrayBuffer;
  if (d instanceof Blob) return d.arrayBuffer();
  if (d instanceof ArrayBuffer) return d.slice(0);
  const v = d as ArrayBufferView;
  return v.buffer.slice(v.byteOffset, v.byteOffset + v.byteLength) as ArrayBuffer;
};

/** A writable for an OPFS file handle, written by the worker. */
async function writableFor(h: FileSystemFileHandle, keep: boolean) {
  const root = await navigator.storage.getDirectory();
  const path = await root.resolve(h);
  if (!path) throw new DOMException('Only files in the browser’s own storage can be written here.', 'NotAllowedError');
  const id = ++fid;
  await call({ op: 'open', id, path, keep });
  let pos = 0;
  const write = async (c: Chunk) => {
    if (typeof c === 'object' && c !== null && 'type' in c && !(c instanceof Blob)) {
      if (c.type === 'seek') return void (pos = c.position);
      if (c.type === 'truncate') {
        await call({ op: 'truncate', id, size: c.size });
        pos = Math.min(pos, c.size);
        return;
      }
      if (c.position !== undefined) pos = c.position;
      c = c.data;
    }
    const bytes = await bytesOf(c as Blob | BufferSource | string);
    const at = pos;
    pos += bytes.byteLength;
    await call({ op: 'write', id, at, bytes }, [bytes]);
  };
  let done = false;
  const finish = async (op: 'close' | 'abort') => {
    if (done) return;
    done = true;
    await call({ op, id });
  };
  const stream = new WritableStream<Chunk>({
    write: (c) => write(c),
    close: () => finish('close'),
    abort: () => finish('abort'),
  });
  // the browser's FileSystemWritableFileStream has these straight on it as well
  return Object.assign(stream, {
    write,
    seek: async (p: number) => void (pos = p),
    truncate: (size: number) => write({ type: 'truncate', size }),
    close: async () => {
      // (written through the stream: let it finish; written straight: just close the file)
      if (stream.locked) return;
      await finish('close');
    },
    abort: async () => finish('abort'),
  });
}

/** Give FileSystemFileHandle a createWritable where the browser has none (and can write in a worker). */
export function installWritable() {
  if (typeof FileSystemFileHandle === 'undefined') return;
  const proto = FileSystemFileHandle.prototype as unknown as {
    createWritable?: (o?: { keepExistingData?: boolean }) => Promise<unknown>;
  };
  if (typeof proto.createWritable === 'function') return;
  proto.createWritable = function (this: FileSystemFileHandle, o?: { keepExistingData?: boolean }) {
    return writableFor(this, !!o?.keepExistingData);
  };
}
